<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import Lucide from "@/components/Base/Lucide";
import api from "@/utils/axios";
import GenealogyStatsPanel, {
  type GenealogyStats,
} from "@/modules/Dashboard/components/GenealogyStatsPanel.vue";

const { t } = useI18n();
const authStore = useAuthStore();

const genealogyStats = ref<GenealogyStats | null>(null);
const isLoadingStats = ref(false);
const statsError = ref("");

const fetchStats = async (): Promise<void> => {
  isLoadingStats.value = true;
  statsError.value = "";
  try {
    const res = await api.get("/dashboard/stats");
    genealogyStats.value = res.data.data?.genealogy ?? null;
  } catch {
    statsError.value = t("dashboard.genealogy.load_error");
  } finally {
    isLoadingStats.value = false;
  }
};

onMounted(() => {
  void fetchStats();
});
</script>

<template>
  <div class="grid grid-cols-12 gap-6 mt-8">
    <div class="col-span-12 intro-y box p-8">
      <div class="flex items-center gap-2 text-2xl font-semibold">
        <Lucide icon="GaugeCircle" class="w-8 h-8 stroke-[1.3]" />
        {{ t("dashboard.title") }}
      </div>
      <p class="mt-4 text-slate-800">
        {{ t("dashboard.welcome") }}
        {{ authStore.user?.first_name || "" }}
      </p>
      <p class="mt-2 text-slate-700">
        {{ t("dashboard.welcome_msg") }}
      </p>
    </div>

    <div v-if="isLoadingStats" class="col-span-12 intro-y box p-8 text-center text-slate-700">
      <Lucide icon="Loader2" class="w-8 h-8 mx-auto animate-spin stroke-[1.3]" />
      <p class="mt-3">{{ t("dashboard.genealogy.loading") }}</p>
    </div>

    <div v-else-if="statsError" class="col-span-12 intro-y box p-8 text-danger">
      {{ statsError }}
    </div>

    <GenealogyStatsPanel v-else-if="genealogyStats" :stats="genealogyStats" />
  </div>
</template>
